/**
 * Admin → LDAP Settings (direct LDAPS bind)
 *
 * One row per tenant. Search-then-bind authentication against AD or
 * OpenLDAP. The page-size field drives RFC 2696 Simple Paged Results
 * Control - AD's MaxPageSize default is 1000; keep page_size ≤ 1000.
 *
 * Bind password is opaque on read - the server returns settings WITHOUT
 * the password field. To rotate, type a new password in the form;
 * leaving it blank preserves the existing one.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Loader2,
  Lock,
  RefreshCw,
  Save,
  Trash2,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  deleteLdapSettings,
  getLdapSettings,
  upsertLdapSettings,
  type LdapSettings as LdapSettingsRow,
} from '@/lib/auth-admin-api';

export function LdapSettings(): JSX.Element {
  const [settings, setSettings] = useState<LdapSettingsRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Form state - initialized from `settings` on load.
  const [host, setHost] = useState('');
  const [port, setPort] = useState(636);
  const [useLdaps, setUseLdaps] = useState(true);
  const [tlsMinVersion, setTlsMinVersion] = useState<'TLSv1.2' | 'TLSv1.3'>('TLSv1.2');
  const [rejectUnauthorized, setRejectUnauthorized] = useState(true);
  const [bindDn, setBindDn] = useState('');
  const [bindPassword, setBindPassword] = useState('');
  const [userSearchBase, setUserSearchBase] = useState('');
  const [userSearchFilter, setUserSearchFilter] = useState('(sAMAccountName={{username}})');
  const [userAttributes, setUserAttributes] = useState('mail,displayName,memberOf,sAMAccountName');
  const [groupSearchBase, setGroupSearchBase] = useState('');
  const [groupSearchFilter, setGroupSearchFilter] = useState('(member={{userDN}})');
  const [pageSize, setPageSize] = useState(500);
  const [requireSaslSigning, setRequireSaslSigning] = useState(false);
  const [requireChannelBinding, setRequireChannelBinding] = useState(false);
  const [enabled, setEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const row = await getLdapSettings();
      setSettings(row);
      if (row) {
        setHost(row.host);
        setPort(row.port);
        setUseLdaps(row.useLdaps);
        setTlsMinVersion((row.tlsMinVersion as 'TLSv1.2' | 'TLSv1.3') ?? 'TLSv1.2');
        setRejectUnauthorized(row.rejectUnauthorized);
        setBindDn(row.bindDn);
        // bindPassword intentionally left empty - admin must re-type to rotate
        setUserSearchBase(row.userSearchBase);
        setUserSearchFilter(row.userSearchFilter);
        setUserAttributes(row.userAttributes);
        setGroupSearchBase(row.groupSearchBase ?? '');
        setGroupSearchFilter(row.groupSearchFilter ?? '(member={{userDN}})');
        setPageSize(row.pageSize);
        setRequireSaslSigning(row.requireSaslSigning);
        setRequireChannelBinding(row.requireChannelBinding);
        setEnabled(row.enabled);
      }
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const updated = await upsertLdapSettings({
        enabled,
        host,
        port,
        useLdaps,
        tlsMinVersion,
        rejectUnauthorized,
        bindDn,
        ...(bindPassword.length > 0 ? { bindPassword } : {}),
        userSearchBase,
        userSearchFilter,
        userAttributes,
        ...(groupSearchBase ? { groupSearchBase } : {}),
        ...(groupSearchFilter ? { groupSearchFilter } : {}),
        pageSize,
        requireSaslSigning,
        requireChannelBinding,
      });
      setSettings(updated);
      setBindPassword('');
      setNotice('Saved.');
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setSaving(false);
    }
  }

  async function clear(): Promise<void> {
    if (!confirm('Delete LDAP settings for this tenant? Active LDAP logins will fail until re-configured.')) return;
    try {
      await deleteLdapSettings();
      setSettings(null);
      await load();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    }
  }

  const newConfig = !settings;

  return (
    <PageContainer>
      <PageHeader
        title="LDAP / Active Directory"
        subtitle="Direct LDAPS bind. Search-then-bind via RFC 2696 paged search - works against AD directories of any size."
      />

      <GlassCard className="max-w-3xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                <Database className="-mt-0.5 mr-2 inline h-4 w-4" />
                Connection
                {settings && (
                  settings.enabled ? (
                    <Badge variant="success" className="ml-2 text-[10px]">
                      <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />
                      Enabled
                    </Badge>
                  ) : (
                    <Badge variant="warning" className="ml-2 text-[10px]">
                      Disabled
                    </Badge>
                  )
                )}
              </CardTitle>
              <CardDescription>
                {newConfig
                  ? 'No LDAP settings yet. Fill the form and Save to create.'
                  : 'Type a new bind password to rotate; leave it blank to preserve the existing one.'}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={load} disabled={loading} title="Refresh">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
              {!newConfig && (
                <Button size="sm" variant="ghost" onClick={clear} title="Delete" data-action="disc.adm_ldap.a02">
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <div className="space-y-4 p-5 pt-0">
          {error && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-500">
              <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
              {error}
            </div>
          )}
          {notice && (
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-500">
              <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />
              {notice}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <TextField label="Host" value={host} onChange={setHost} placeholder="dc.corp.example" />
            <NumberField label="Port" value={port} onChange={setPort} />
            <SelectField
              label="TLS minimum"
              value={tlsMinVersion}
              onChange={(v) => setTlsMinVersion(v as 'TLSv1.2' | 'TLSv1.3')}
              options={['TLSv1.2', 'TLSv1.3']}
            />
            <NumberField label="Page size (≤ 1000 for AD)" value={pageSize} onChange={setPageSize} />
          </div>

          <TextField label="Bind DN" value={bindDn} onChange={setBindDn} placeholder="cn=foctta,ou=Service,dc=corp,dc=example" />
          <PasswordField
            label={newConfig ? 'Bind password' : 'New bind password (leave blank to preserve)'}
            value={bindPassword}
            onChange={setBindPassword}
          />

          <TextField label="User search base" value={userSearchBase} onChange={setUserSearchBase} placeholder="ou=Users,dc=corp,dc=example" />
          <TextField label="User search filter" value={userSearchFilter} onChange={setUserSearchFilter} />
          <TextField label="User attributes (comma-separated)" value={userAttributes} onChange={setUserAttributes} />

          <TextField label="Group search base (optional)" value={groupSearchBase} onChange={setGroupSearchBase} placeholder="ou=Groups,dc=corp,dc=example" />
          <TextField label="Group search filter (optional)" value={groupSearchFilter} onChange={setGroupSearchFilter} />

          <div className="flex flex-wrap gap-4">
            <Checkbox label="LDAPS (use TLS)" checked={useLdaps} onChange={setUseLdaps} />
            <Checkbox label="Reject unauthorized" checked={rejectUnauthorized} onChange={setRejectUnauthorized} />
            <Checkbox label="Require SASL signing (Server 2025)" checked={requireSaslSigning} onChange={setRequireSaslSigning} />
            <Checkbox label="Require channel binding" checked={requireChannelBinding} onChange={setRequireChannelBinding} />
            <Checkbox label="Enable LDAP for this tenant" checked={enabled} onChange={setEnabled} />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" onClick={save} disabled={saving || !host || !bindDn || !userSearchBase || (newConfig && !bindPassword)} data-action="disc.adm_ldap.a01">
              {saving ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Save className="-mt-0.5 mr-1 inline h-3 w-3" />}
              Save
            </Button>
            <p className="text-[10px] text-muted-foreground">
              <Lock className="-mt-0.5 mr-1 inline h-3 w-3" />
              Bind password encrypted with AES-256-GCM via the tenant's KMS key.
            </p>
          </div>

          {settings?.lastFailureMessage && (
            <div className="mt-2 rounded-md bg-red-500/5 border border-red-500/20 p-3 text-xs text-red-500/80">
              <XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />
              Last LDAP failure: {settings.lastFailureMessage}
            </div>
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}

function TextField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80">{props.label}</label>
      <input
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
      />
    </div>
  );
}

function NumberField(props: { label: string; value: number; onChange: (n: number) => void }): JSX.Element {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80">{props.label}</label>
      <input
        type="number"
        value={props.value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (Number.isFinite(n)) props.onChange(n);
        }}
        className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
      />
    </div>
  );
}

function PasswordField(props: { label: string; value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80">{props.label}</label>
      <input
        type="password"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
      />
    </div>
  );
}

function SelectField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}): JSX.Element {
  return (
    <div>
      <label className="text-xs font-medium text-foreground/80">{props.label}</label>
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
      >
        {props.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Checkbox(props: { label: string; checked: boolean; onChange: (b: boolean) => void }): JSX.Element {
  return (
    <label className="inline-flex items-center gap-2 text-xs">
      <input type="checkbox" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
      {props.label}
    </label>
  );
}
